import { create } from 'zustand';
import { supabase } from '../lib/supabase.js';
import { getClientUid } from '../lib/uid.js';
import {
  getDisplayName,
  markSuggestionSent,
  pushHistoryEntry,
  setDisplayName,
} from '../lib/profile.js';
import {
  ensureAnonymousAuth,
  fetchBarUserByAuth,
  insertGuestBarUser,
} from '../lib/barIdentity.js';
import { bumpRealtimeRx, isRealtimeDebugEnabled, rtDebug } from '../lib/realtimeDebug.js';
import {
  ACTIVE_STATUSES,
  aggregateBidsByRequestId,
  normalizeRequest,
  normalizeRequests,
  presenceCount,
  pruneDonorFeedEntries,
  pulsePatchForDonationLoad,
  sortByPriority,
} from './eventStoreHelpers.js';

// Single-flight : initBarIdentity est déclenché à la fois au montage et par
// onAuthStateChange. Sans garde, deux exécutions concurrentes peuvent créer des
// états contradictoires (double sign-in anonyme, course sur l'insert bar_users).
let barIdentityInFlight = null;

let contributorsDebounceTimer = null;
let votePodiumDebounceTimer = null;
let donationGoalResyncTimer = null;
let gamificationRefreshTimer = null;

// Annule un loadEvent en vol quand un nouveau load ou un reset survient : sans ça,
// un unmount rapide laisse le canal realtime s'abonner après le reset (canal orphelin,
// presence gonflée). On compare la génération capturée au début à la génération courante.
let loadGeneration = 0;
// Single-flight de subscribeRealtime : deux appels concurrents créaient un canal en double.
let subscribeInFlight = null;

// Colonnes bids exposées au client : tout SAUF stripe_payment_intent_id (ID Stripe
// interne, jamais utile côté invité). Aligné avec le revoke de colonne côté DB.
const BID_SELECT = 'id, request_id, event_id, client_uid, amount, payment_status, created_at';

function clearEventDebounceTimers() {
  clearTimeout(contributorsDebounceTimer);
  clearTimeout(votePodiumDebounceTimer);
  clearTimeout(donationGoalResyncTimer);
  clearTimeout(gamificationRefreshTimer);
  contributorsDebounceTimer = null;
  votePodiumDebounceTimer = null;
  donationGoalResyncTimer = null;
  gamificationRefreshTimer = null;
}

/** Aligné sur public.check_and_trigger_goals — source de vérité unique (schema.sql). */
async function fetchPaidBidsTotalForEvent(eventId) {
  const { data, error } = await supabase
    .from('bids')
    .select('amount')
    .eq('event_id', eventId)
    .eq('payment_status', 'paid');

  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[event-pot] lecture bids impossible', error.message);
    return 0;
  }
  return (data ?? []).reduce((acc, row) => acc + Math.max(0, Number(row.amount) || 0), 0);
}

export const useEventStore = create((set, get) => ({
  loading: true,
  error: null,
  event: null,
  establishment: null,
  requests: [],
  votedRequestIds: new Set(),
  channel: null,
  liveStatus: 'idle', // 'idle' | 'connecting' | 'live' | 'reconnecting' | 'closed'
  participants: 0,
  donationGoal: null,
  donationGoalPulse: 0,
  /** Somme des bids payés pour l'événement (même règle que la cagnotte / paliers DB). */
  eventPotCents: 0,

  /** @type {null | { id: string, username: string, avatar_url?: string|null, is_guest?: boolean }}} */
  barUser: null,
  barAuthReady: false,
  /** Raison brute si initBarIdentity échoue (affichage UI). */
  barAuthReason: null,
  /** Message d’erreur Supabase si la connexion anonyme échoue (diagnostic UI). */
  barAuthSignInError: null,
  /** Détail lecture / insert bar_users (RLS, grants, contraintes). */
  barAuthProfileError: null,
  /** E-mail Supabase si compte déjà lié (magic link, etc.). */
  authEmail: null,

  /** Lignes bids de l’événement (agrégation pour bid_amount / tri). */
  eventBids: [],

  /** @type {{ id: string, line: string, at: number }[]} */
  liveBoostFeed: [],

  /** @type {{ bar_user_id: string, username: string, totalCents: number }[]} */
  topContributors: [],

  /** Podium : total des ♥ sur les suggestions d’un même profil (bar_user_id). */
  /** @type {{ bar_user_id: string, username: string, voteCount: number }[]} */
  votePodium: [],

  /**
   * Annonce broadcast (tous les clients) lorsque le DJ accepte une suggestion ayant
   * un montant payé associé (passage status pending|rejected → play_next).
   * @type {{ id: string, song: string, artist: string, donor: string, donorBarUserId: string|null, amountCents: number, at: number } | null}
   */
  acceptedSong: null,

  /** @type {Record<string, string>} */
  usernameByBarUserId: {},

  /** Badge épinglé par participant (bar_user_id → badge_code|null) : pastille live. */
  /** @type {Record<string, string|null>} */
  featuredBadgeByBarUserId: {},

  /**
   * Compteurs de réactions par suggestion : requestId → reaction_key → nombre.
   * @type {Record<string, Record<string, number>>}
   */
  reactionCountsByRequest: {},

  /**
   * Réactions déjà posées par ce client (client_uid) : requestId → liste de reaction_key.
   * @type {Record<string, string[]>}
   */
  myReactionKeysByRequest: {},

  /**
   * Badges de soirée (lecture seule côté client).
   * @type {{ id: string, bar_user_id: string, badge_code: string, username: string, created_at?: string }[]}
   */
  eventBadges: [],

  async loadEvent(qrCode) {
    const gen = ++loadGeneration;
    set({
      loading: true,
      error: null,
      liveStatus: 'connecting',
      establishment: null,
    });

    // QR établissement uniquement : résolution establishment → event live
    const { data: establishment, error: estabError } = await supabase
      .from('establishments')
      .select('id, name, type')
      .eq('qr_code', qrCode)
      .maybeSingle();

    if (estabError) {
      set({
        loading: false,
        error: estabError.message,
        event: null,
        liveStatus: 'closed',
      });
      return;
    }

    let event = null;
    let eventError = null;

    if (!establishment) {
      set({
        loading: false,
        error: null,
        event: null,
        requests: [],
        liveStatus: 'closed',
        establishment: null,
      });
      return;
    }

    set({ establishment });

    const res = await supabase
      .from('events')
      .select(
        '*, establishments(name, type), djs(name, stage_name, instagram_url, tiktok_url, soundcloud_url, spotify_url)'
      )
      .eq('establishment_id', establishment.id)
      .eq('status', 'live')
      .order('starts_at', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle();
    event = res.data;
    eventError = res.error;

    if (eventError) {
      set({ loading: false, error: eventError.message, event: null, liveStatus: 'closed' });
      return;
    }
    if (!event) {
      set({
        loading: false,
        event: null,
        requests: [],
        eventBids: [],
        liveStatus: 'closed',
        donationGoal: null,
        donationGoalPulse: 0,
      });
      return;
    }

    const donationGoalPromise = get().loadDonationGoal(event.id);

    const [{ data: requests, error: reqError }, { data: evBids, error: bidsError }] =
      await Promise.all([
        supabase
          .from('requests')
          .select('*')
          .eq('event_id', event.id)
          .neq('status', 'played')
          .neq('status', 'rejected'),
        supabase.from('bids').select(BID_SELECT).eq('event_id', event.id),
      ]);

    if (bidsError) {
      // eslint-disable-next-line no-console
      console.warn('[event] lecture bids', bidsError.message);
    }

    if (reqError) {
      set({ loading: false, error: reqError.message, event, liveStatus: 'closed' });
      return;
    }

    const clientUid = getClientUid();
    const { data: myVotes } = await supabase
      .from('votes')
      .select('request_id')
      .eq('event_id', event.id)
      .eq('client_uid', clientUid);

    await donationGoalPromise;

    const bidsList = evBids ?? [];

    const suggesterIds = [...new Set((requests ?? []).map((r) => r.bar_user_id).filter(Boolean))];
    let suggesterNames = {};
    if (suggesterIds.length) {
      const { data: suggRows } = await supabase
        .from('bar_users')
        .select('id, username')
        .in('id', suggesterIds);
      suggesterNames = Object.fromEntries((suggRows ?? []).map((n) => [n.id, n.username]));
    }

    // Un reset ou un nouveau loadEvent est survenu pendant les fetchs : on abandonne
    // pour ne pas écraser l'état courant ni abonner un canal orphelin après le reset.
    if (gen !== loadGeneration) return;

    set({
      loading: false,
      error: null,
      event,
      eventBids: bidsList,
      requests: sortByPriority(normalizeRequests(requests, bidsList)),
      votedRequestIds: new Set((myVotes ?? []).map((v) => v.request_id)),
      liveBoostFeed: [],
      topContributors: [],
      votePodium: [],
      reactionCountsByRequest: {},
      myReactionKeysByRequest: {},
      eventBadges: [],
      usernameByBarUserId: { ...get().usernameByBarUserId, ...suggesterNames },
    });

    await get().initBarIdentity();
    if (gen !== loadGeneration) return;
    await get().refreshTopContributors(event.id);
    await get().refreshVotePodium(event.id);
    await get().refreshGamificationState(event.id);

    if (gen !== loadGeneration) return;
    await get().subscribeRealtime(event.id);
  },

  // Re-fetch complet des données de la soirée courante. Appelé sur reconnexion
  // realtime : tout ce qui a été inséré/modifié pendant une coupure (téléphone
  // verrouillé au bar) est rattrapé, sinon file et cagnotte restent désynchronisées.
  async resyncEventData() {
    const event = get().event;
    if (!event?.id) return;
    const eventId = event.id;
    const clientUid = getClientUid();

    const [{ data: requests, error: reqError }, { data: evBids }, { data: myVotes }] =
      await Promise.all([
        supabase
          .from('requests')
          .select('*')
          .eq('event_id', eventId)
          .neq('status', 'played')
          .neq('status', 'rejected'),
        supabase.from('bids').select(BID_SELECT).eq('event_id', eventId),
        supabase.from('votes').select('request_id').eq('event_id', eventId).eq('client_uid', clientUid),
      ]);

    if (reqError || get().event?.id !== eventId) return;

    const bidsList = evBids ?? [];
    set({
      eventBids: bidsList,
      requests: sortByPriority(normalizeRequests(requests ?? [], bidsList)),
      votedRequestIds: new Set((myVotes ?? []).map((v) => v.request_id)),
    });

    await get().loadDonationGoal(eventId);
    await get().refreshTopContributors(eventId);
    await get().refreshVotePodium(eventId);
    await get().refreshGamificationState(eventId);
  },

  async initBarIdentity() {
    if (barIdentityInFlight) return barIdentityInFlight;
    barIdentityInFlight = (async () => {
    set({ barAuthReady: false, barAuthReason: null, barAuthSignInError: null, barAuthProfileError: null });
    const { signInError } = await ensureAnonymousAuth();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const user = session?.user ?? null;

    const email =
      user?.email && String(user.email).trim() !== '' ? user.email : null;

    if (!user?.id) {
      set({
        barUser: null,
        barAuthReady: true,
        barAuthReason: 'no_auth_session',
        barAuthSignInError: signInError,
        authEmail: null,
      });
      return { ok: false, reason: 'no_auth_session' };
    }

    const authId = user.id;

    const fetched = await fetchBarUserByAuth(authId);
    if (fetched.error) {
      const msg = [fetched.error.code, fetched.error.message].filter(Boolean).join(' · ');
      // eslint-disable-next-line no-console
      console.warn('[bar-users] select', fetched.error);
      set({
        barUser: null,
        barAuthReady: true,
        barAuthReason: 'bar_user_failed',
        barAuthSignInError: null,
        barAuthProfileError: `Lecture profil : ${msg}`,
        authEmail: email,
      });
      return { ok: false, reason: 'bar_user_failed' };
    }

    let row = fetched.data;
    if (!row) {
      const ins = await insertGuestBarUser(authId);
      row = ins.data;
      if (ins.error || !row) {
        // eslint-disable-next-line no-console
        console.warn('[bar-users] create guest', ins.error);
        const insMsg = ins.error
          ? [ins.error.code, ins.error.message, ins.error.details].filter(Boolean).join(' · ')
          : 'insert sans détail';
        set({
          barUser: null,
          barAuthReady: true,
          barAuthReason: 'bar_user_failed',
          barAuthSignInError: null,
          barAuthProfileError: `Création profil : ${insMsg}`,
          authEmail: email,
        });
        return { ok: false, reason: 'bar_user_failed' };
      }
    }

    setDisplayName(row.username);
    set({
      barUser: row,
      barAuthReady: true,
      barAuthReason: null,
      barAuthSignInError: null,
      barAuthProfileError: null,
      authEmail: email,
      usernameByBarUserId: {
        ...get().usernameByBarUserId,
        [row.id]: row.username,
      },
    });

    return { ok: true };
    })().finally(() => {
      barIdentityInFlight = null;
    });
    return barIdentityInFlight;
  },

  async updateBarUsername(nextUsername) {
    const clean = String(nextUsername ?? '')
      .trim()
      .slice(0, 32);
    if (clean.length < 1) {
      return { ok: false, message: 'Pseudo trop court.' };
    }

    const bu = get().barUser;
    if (!bu) {
      setDisplayName(clean);
      return { ok: true, fallbackLocal: true };
    }

    const { data, error } = await supabase
      .from('bar_users')
      .update({ username: clean, is_guest: false })
      .eq('id', bu.id)
      .select('id, username, avatar_url, featured_badge_code, is_guest, auth_user_id, created_at')
      .maybeSingle();

    if (error) {
      const msg =
        error.code === '23505'
          ? 'Ce pseudo est déjà pris, choisis-en un autre.'
          : error.message ?? 'Erreur de mise à jour';
      return { ok: false, message: msg };
    }

    if (data) {
      setDisplayName(data.username);
      set({
        barUser: data,
        usernameByBarUserId: {
          ...get().usernameByBarUserId,
          [data.id]: data.username,
        },
      });
      await get().syncPresenceDisplay();
      const eventId = get().event?.id;
      if (eventId) {
        void get().refreshVotePodium(eventId);
      }
    }

    return { ok: true };
  },

  /** Met à jour avatar / badge épinglé (colonnes libres, pas d'unicité). */
  async patchBarUser(patch) {
    const bu = get().barUser;
    if (!bu) return { ok: false, message: 'Profil non chargé.' };
    const { data, error } = await supabase
      .from('bar_users')
      .update(patch)
      .eq('id', bu.id)
      .select('id, username, avatar_url, featured_badge_code, is_guest, auth_user_id, created_at')
      .maybeSingle();
    if (error) return { ok: false, message: error.message ?? 'Erreur de mise à jour' };
    if (data) {
      set({ barUser: data });
      const eventId = get().event?.id;
      if (eventId && 'featured_badge_code' in patch) {
        set((s) => ({
          featuredBadgeByBarUserId: {
            ...s.featuredBadgeByBarUserId,
            [data.id]: data.featured_badge_code ?? null,
          },
        }));
      }
    }
    return { ok: true };
  },

  async refreshTopContributors(eventId) {
    const { data: rows, error } = await supabase
      .from('bids')
      .select('bar_user_id, amount')
      .eq('event_id', eventId)
      .eq('payment_status', 'paid');

    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[contributors] lecture bids impossible', error.message);
      set({ topContributors: [] });
      return;
    }

    if (!rows?.length) {
      set({ topContributors: [] });
      return;
    }

    const map = new Map();
    for (const r of rows) {
      const id = r.bar_user_id;
      if (!id) continue;
      map.set(id, (map.get(id) ?? 0) + (Number(r.amount) || 0));
    }

    const sorted = [...map.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);
    const ids = sorted.map(([id]) => id);
    if (ids.length === 0) {
      set({ topContributors: [] });
      return;
    }

    const { data: names } = await supabase
      .from('bar_users')
      .select('id, username, featured_badge_code')
      .in('id', ids);

    const byId = Object.fromEntries((names ?? []).map((n) => [n.id, n.username]));
    const featById = Object.fromEntries((names ?? []).map((n) => [n.id, n.featured_badge_code ?? null]));

    const extraLookup = {};
    for (const [id] of sorted) extraLookup[id] = byId[id] ?? '?';

    set({
      topContributors: sorted.map(([id, cents]) => ({
        bar_user_id: id,
        username: byId[id] ?? '?',
        totalCents: cents,
      })),
      usernameByBarUserId: { ...get().usernameByBarUserId, ...extraLookup },
      featuredBadgeByBarUserId: { ...get().featuredBadgeByBarUserId, ...featById },
    });
  },

  async refreshVotePodium(eventId) {
    if (!eventId) return;

    const { data: rows, error } = await supabase
      .from('requests')
      .select('bar_user_id, votes')
      .eq('event_id', eventId);

    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[vote-podium] lecture requests impossible', error.message);
      set({ votePodium: [] });
      return;
    }

    const map = new Map();
    for (const r of rows ?? []) {
      const id = r.bar_user_id;
      if (!id) continue;
      const v = Math.max(0, Number(r.votes) || 0);
      map.set(id, (map.get(id) ?? 0) + v);
    }

    const sorted = [...map.entries()]
      .filter(([, votes]) => votes > 0)
      .sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])))
      .slice(0, 10);

    if (sorted.length === 0) {
      set({ votePodium: [] });
      return;
    }

    const ids = sorted.map(([id]) => id);
    const { data: names } = await supabase
      .from('bar_users')
      .select('id, username, featured_badge_code')
      .in('id', ids);
    const byId = Object.fromEntries((names ?? []).map((n) => [n.id, n.username]));
    const featById = Object.fromEntries((names ?? []).map((n) => [n.id, n.featured_badge_code ?? null]));

    set({
      votePodium: sorted.map(([id, voteCount]) => ({
        bar_user_id: id,
        username: byId[id] ?? '?',
        voteCount,
      })),
      usernameByBarUserId: {
        ...get().usernameByBarUserId,
        ...Object.fromEntries(ids.map((id) => [id, byId[id] ?? '?'])),
      },
      featuredBadgeByBarUserId: { ...get().featuredBadgeByBarUserId, ...featById },
    });
  },

  scheduleVotePodiumRefresh(eventId) {
    if (!eventId) return;
    clearTimeout(votePodiumDebounceTimer);
    votePodiumDebounceTimer = setTimeout(() => {
      void get().refreshVotePodium(eventId);
    }, 350);
  },

  scheduleContributorsRefresh(eventId) {
    clearTimeout(contributorsDebounceTimer);
    contributorsDebounceTimer = setTimeout(() => {
      get().refreshTopContributors(eventId);
    }, 400);
  },

  scheduleDonationGoalResync() {
    const eventId = get().event?.id;
    if (!eventId) return;
    clearTimeout(donationGoalResyncTimer);
    donationGoalResyncTimer = setTimeout(() => {
      void get().loadDonationGoal(eventId);
    }, 320);
  },

  scheduleGamificationRefresh(eventId) {
    if (!eventId) return;
    clearTimeout(gamificationRefreshTimer);
    gamificationRefreshTimer = setTimeout(() => {
      void get().refreshGamificationState(eventId);
    }, 260);
  },

  async refreshGamificationState(eventId) {
    if (!eventId) return;
    const clientUid = getClientUid();

    const [{ data: rxRows, error: rxErr }, { data: badgeRows, error: badgeErr }] = await Promise.all([
      supabase
        .from('request_reactions')
        .select('request_id, reaction_key, client_uid')
        .eq('event_id', eventId),
      supabase
        .from('event_user_badges')
        .select('id, bar_user_id, badge_code, created_at')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false })
        .limit(40),
    ]);

    if (rxErr) {
      // eslint-disable-next-line no-console
      console.warn('[gamification] request_reactions', rxErr.message);
    }
    if (badgeErr) {
      // eslint-disable-next-line no-console
      console.warn('[gamification] event_user_badges', badgeErr.message);
    }

    const reactionCountsByRequest = {};
    const myReactionKeysByRequest = {};
    for (const row of rxRows ?? []) {
      const rid = row.request_id;
      const key = row.reaction_key;
      if (!rid || !key) continue;
      if (!reactionCountsByRequest[rid]) reactionCountsByRequest[rid] = {};
      reactionCountsByRequest[rid][key] = (reactionCountsByRequest[rid][key] ?? 0) + 1;
      if (row.client_uid === clientUid) {
        if (!myReactionKeysByRequest[rid]) myReactionKeysByRequest[rid] = [];
        if (!myReactionKeysByRequest[rid].includes(key)) {
          myReactionKeysByRequest[rid].push(key);
        }
      }
    }

    const badges = badgeRows ?? [];
    const badgeUserIds = [...new Set(badges.map((b) => b.bar_user_id).filter(Boolean))];
    const namePatch = { ...get().usernameByBarUserId };
    const missing = badgeUserIds.filter((id) => !namePatch[id]);
    if (missing.length) {
      const { data: names } = await supabase.from('bar_users').select('id, username').in('id', missing);
      for (const n of names ?? []) {
        namePatch[n.id] = n.username;
      }
    }

    const eventBadges = badges.map((b) => ({
      id: b.id,
      bar_user_id: b.bar_user_id,
      badge_code: b.badge_code,
      username: namePatch[b.bar_user_id] ?? '?',
      created_at: b.created_at,
    }));

    set({
      reactionCountsByRequest,
      myReactionKeysByRequest,
      eventBadges,
      usernameByBarUserId: namePatch,
    });
  },

  applyGamificationBadgeRow(row) {
    if (!row?.id || !row.bar_user_id || !row.badge_code) return;
    const evt = get().event;
    if (!evt || row.event_id !== evt.id) return;

    void (async () => {
      let username = get().usernameByBarUserId[row.bar_user_id];
      if (!username) {
        const { data } = await supabase
          .from('bar_users')
          .select('username')
          .eq('id', row.bar_user_id)
          .maybeSingle();
        username = data?.username ?? '?';
        set({
          usernameByBarUserId: { ...get().usernameByBarUserId, [row.bar_user_id]: username },
        });
      }
      const entry = {
        id: row.id,
        bar_user_id: row.bar_user_id,
        badge_code: row.badge_code,
        username,
        created_at: row.created_at,
      };
      set((s) => ({
        eventBadges: [entry, ...s.eventBadges.filter((b) => b.id !== row.id)].slice(0, 40),
      }));
    })();
  },

  async addRequestReaction(requestId, reactionKey) {
    const event = get().event;
    if (!event?.id || event.status !== 'live') {
      return { ok: false, reason: 'no_live_event' };
    }
    const mine = get().myReactionKeysByRequest[requestId] ?? [];
    if (mine.includes(reactionKey)) {
      return { ok: false, reason: 'already_reacted' };
    }

    const clientUid = getClientUid();
    const barUid = get().barUser?.id ?? null;

    const { data, error } = await supabase
      .from('request_reactions')
      .insert({
        event_id: event.id,
        request_id: requestId,
        reaction_key: reactionKey,
        client_uid: clientUid,
        bar_user_id: barUid,
      })
      .select('id, request_id, reaction_key, client_uid')
      .maybeSingle();

    if (error) {
      if (error.code === '23505') {
        get().scheduleGamificationRefresh(event.id);
        return { ok: false, reason: 'already_reacted' };
      }
      return { ok: false, reason: 'error', error };
    }

    if (data) {
      const rid = data.request_id;
      const key = data.reaction_key;
      set((s) => {
        const counts = { ...s.reactionCountsByRequest };
        const cur = { ...(counts[rid] ?? {}) };
        cur[key] = (cur[key] ?? 0) + 1;
        counts[rid] = cur;
        const my = { ...s.myReactionKeysByRequest };
        const arr = [...(my[rid] ?? [])];
        if (!arr.includes(key)) arr.push(key);
        my[rid] = arr;
        return { reactionCountsByRequest: counts, myReactionKeysByRequest: my };
      });
    }

    get().scheduleGamificationRefresh(event.id);
    return { ok: true };
  },

  async removeRequestReaction(requestId, reactionKey) {
    const event = get().event;
    if (!event?.id || event.status !== 'live') {
      return { ok: false, reason: 'no_live_event' };
    }
    const mine = get().myReactionKeysByRequest[requestId] ?? [];
    if (!mine.includes(reactionKey)) {
      return { ok: false, reason: 'not_mine' };
    }

    const clientUid = getClientUid();

    const { data, error } = await supabase.rpc('remove_request_reaction', {
      p_event_id: event.id,
      p_request_id: requestId,
      p_reaction_key: reactionKey,
      p_client_uid: clientUid,
    });

    if (error) {
      return { ok: false, reason: 'error', error };
    }

    const removed = Number(data) > 0;
    if (!removed) {
      get().scheduleGamificationRefresh(event.id);
      return { ok: false, reason: 'not_found' };
    }

    set((s) => {
      const rid = requestId;
      const key = reactionKey;
      const counts = { ...s.reactionCountsByRequest };
      const cur = { ...(counts[rid] ?? {}) };
      const next = Math.max(0, (cur[key] ?? 0) - 1);
      if (next <= 0) delete cur[key];
      else cur[key] = next;
      if (Object.keys(cur).length === 0) delete counts[rid];
      else counts[rid] = cur;

      const my = { ...s.myReactionKeysByRequest };
      const arr = (my[rid] ?? []).filter((k) => k !== key);
      if (arr.length === 0) delete my[rid];
      else my[rid] = arr;

      return { reactionCountsByRequest: counts, myReactionKeysByRequest: my };
    });

    get().scheduleGamificationRefresh(event.id);
    return { ok: true };
  },

  async toggleRequestReaction(requestId, reactionKey) {
    const mine = (get().myReactionKeysByRequest[requestId] ?? []).includes(reactionKey);
    if (mine) return get().removeRequestReaction(requestId, reactionKey);
    return get().addRequestReaction(requestId, reactionKey);
  },

  ensureBarUsernameLoaded(barUserId) {
    const bid = barUserId;
    if (!bid || get().usernameByBarUserId[bid]) return;
    void (async () => {
      const { data } = await supabase.from('bar_users').select('username').eq('id', bid).maybeSingle();
      if (data?.username) {
        set({ usernameByBarUserId: { ...get().usernameByBarUserId, [bid]: data.username } });
      }
    })();
  },

  pushBoostFeedFromBid(row) {
    if (!row || row.payment_status !== 'paid') return;
    const amount = Number(row.amount) || 0;
    const reqTitle =
      get().requests.find((r) => r.id === row.request_id)?.custom_title ?? 'Une track';

    const resolveName = async () => {
      let nm = '';
      if (row.bar_user_id) {
        nm =
          get().usernameByBarUserId[row.bar_user_id] ??
          (await (async () => {
            const { data } = await supabase
              .from('bar_users')
              .select('username')
              .eq('id', row.bar_user_id)
              .maybeSingle();
            return data?.username ?? '';
          })());
        if (nm) {
          set({
            usernameByBarUserId: {
              ...get().usernameByBarUserId,
              [row.bar_user_id]: nm,
            },
          });
        }
      }
      if (
        !nm &&
        row.bar_user_id &&
        get().barUser?.id &&
        get().barUser.id === row.bar_user_id
      ) {
        nm = get().barUser?.username ?? '';
      }
      if (!nm) nm = row.bar_user_id ? 'Participant' : 'Un invité';

      const euros = `${(amount / 100).toLocaleString('fr-FR', {
        minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      })}`;

      const safeTitle = String(reqTitle).replace(/[«»]/g, "'").slice(0, 56);
      const line = `🔥 ${nm} a soutenu « ${safeTitle} » · +${euros} €`;

      const now = Date.now();
      const next = [{ id: row.id, line, at: now }, ...pruneDonorFeedEntries(get().liveBoostFeed, now)];
      set({ liveBoostFeed: next.slice(0, 18) });
    };

    void resolveName();
  },

  /** Retire du fil « En direct » les lignes de plus de 5 min (sans attendre un nouveau boost). */
  pruneStaleDonorFeed() {
    const pruned = pruneDonorFeedEntries(get().liveBoostFeed);
    if (pruned.length !== get().liveBoostFeed.length) {
      set({ liveBoostFeed: pruned });
    }
  },

  async syncPresenceDisplay() {
    const ch = get().channel;
    const clientUid = getClientUid();
    const nm = get().barUser?.username ?? getDisplayName() ?? 'Anonyme';
    if (!ch) return;

    try {
      await ch.track({
        client_uid: clientUid,
        display_name: nm,
        joined_at: new Date().toISOString(),
      });
    } catch {
      // ignoré (canal fermé ou non prêt)
    }
  },

  async loadDonationGoal(eventId, opts = {}) {
    const retainPulse = Boolean(opts.retainPulse);
    const paidBidsTotalCents = await fetchPaidBidsTotalForEvent(eventId);
    set({ eventPotCents: paidBidsTotalCents });

    const { data: goalEvent, error: goalError } = await supabase
      .from('donation_goal_events')
      .select('event_id, template_id, is_active')
      .eq('event_id', eventId)
      .maybeSingle();

    if (goalError) {
      // eslint-disable-next-line no-console
      console.warn('[donation-goal] lecture donation_goal_events impossible', goalError.message);
      set({
        donationGoal: {
          event_id: eventId,
          is_active: false,
          current_amount_cents: paidBidsTotalCents,
          max_threshold_cents: 1,
          steps: [],
          has_error: true,
          no_goal_row: false,
        },
        ...pulsePatchForDonationLoad(retainPulse),
      });
      return;
    }

    if (!goalEvent) {
      // Aucune ligne en base pour cet événement : le widget reste visible pour diagnostic UX.
      set({
        donationGoal: {
          event_id: eventId,
          is_active: false,
          current_amount_cents: paidBidsTotalCents,
          max_threshold_cents: 1,
          steps: [],
          has_error: false,
          no_goal_row: true,
        },
        ...pulsePatchForDonationLoad(retainPulse),
      });
      return;
    }

    const [
      { data: steps, error: stepsError },
      { data: triggers, error: triggersError },
    ] = await Promise.all([
      supabase
        .from('donation_goal_steps')
        .select('id, threshold_cents, title, description, effect_type, sort_order')
        .eq('template_id', goalEvent.template_id)
        .order('sort_order', { ascending: true }),
      supabase.from('donation_goal_triggers').select('step_id').eq('event_id', eventId),
    ]);

    if (stepsError || triggersError) {
      set({
        donationGoal: {
          event_id: goalEvent.event_id,
          template_id: goalEvent.template_id,
          is_active: Boolean(goalEvent.is_active),
          current_amount_cents: paidBidsTotalCents,
          max_threshold_cents: 1,
          steps: [],
          has_error: true,
          no_goal_row: false,
        },
        ...pulsePatchForDonationLoad(retainPulse),
      });
      return;
    }

    const triggeredSet = new Set((triggers ?? []).map((t) => t.step_id));
    const normalizedSteps = (steps ?? []).map((step) => ({
      ...step,
      is_triggered: triggeredSet.has(step.id),
    }));
    const maxThreshold = normalizedSteps.length
      ? Math.max(...normalizedSteps.map((step) => step.threshold_cents ?? 0))
      : 0;

    set({
      donationGoal: {
        event_id: goalEvent.event_id,
        template_id: goalEvent.template_id,
        is_active: Boolean(goalEvent.is_active),
        current_amount_cents: paidBidsTotalCents,
        max_threshold_cents: maxThreshold,
        steps: normalizedSteps,
        has_error: false,
        no_goal_row: false,
      },
      ...pulsePatchForDonationLoad(retainPulse),
    });
  },

  async subscribeRealtime(eventId) {
    // Single-flight : deux appels concurrents (StrictMode, race loadEvent) créaient
    // un canal en double avec handlers dupliqués.
    if (subscribeInFlight) {
      await subscribeInFlight;
      return;
    }
    subscribeInFlight = get()._doSubscribeRealtime(eventId);
    try {
      await subscribeInFlight;
    } finally {
      subscribeInFlight = null;
    }
  },

  async _doSubscribeRealtime(eventId) {
    // Cleanup du canal précédent : on attend la fin pour éviter que supabase.channel()
    // retourne un canal déjà subscribed (cas StrictMode + double mount).
    const previous = get().channel;
    if (previous) {
      try {
        await supabase.removeChannel(previous);
      } catch {
        // ignore, on continue
      }
      set({ channel: null });
    }

    // Évite la double subscription : si un canal a été créé entre-temps (race),
    // on n'en recrée pas un.
    if (get().channel) return;

    const clientUid = getClientUid();
    set({ liveStatus: 'connecting', participants: 0 });

    // Topic STABLE par soirée : la presence Realtime est scopée par topic, donc
    // tous les clients d'une même soirée doivent partager le même topic pour se
    // voir mutuellement (sinon « en ligne » reste bloqué à 1). Le cleanup
    // removeChannel(previous) ci-dessus évite le canal résiduel déjà subscribed
    // (ex-raison du suffixe aléatoire, qui isolait chaque client).
    const channelTopic = `event-${eventId}`;

    rtDebug('canal: souscription', {
      channel: channelTopic,
      filter: `event_id=eq.${eventId}`,
      debug: isRealtimeDebugEnabled(),
    });

    const channel = supabase
      .channel(channelTopic, {
        config: { presence: { key: clientUid } },
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'requests',
          filter: `event_id=eq.${eventId}`,
        },
        (payload) => {
          bumpRealtimeRx('requests', payload.eventType);
          rtDebug('postgres_changes requests', {
            eventType: payload.eventType,
            id: payload.new?.id ?? payload.old?.id,
          });
          get().applyRealtime(payload);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bids',
          filter: `event_id=eq.${eventId}`,
        },
        (payload) => {
          bumpRealtimeRx('bids', payload.eventType);
          rtDebug('postgres_changes bids', {
            eventType: payload.eventType,
            id: payload.new?.id ?? payload.old?.id,
            payment_status: payload.new?.payment_status,
          });
          get().applyDonationBidRealtime(payload);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'votes',
          filter: `event_id=eq.${eventId}`,
        },
        (payload) => {
          bumpRealtimeRx('votes', payload.eventType);
          rtDebug('postgres_changes votes', { eventType: payload.eventType });
          get().scheduleVotePodiumRefresh(eventId);
          if (payload.eventType === 'DELETE' && payload.old?.request_id) {
            const uid = getClientUid();
            const bu = get().barUser?.id ?? null;
            const row = payload.old;
            const mine =
              row.client_uid === uid || (bu != null && row.bar_user_id === bu);
            if (mine) {
              set((s) => {
                const next = new Set(s.votedRequestIds);
                next.delete(row.request_id);
                return { votedRequestIds: next };
              });
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'donation_goal_triggers',
          filter: `event_id=eq.${eventId}`,
        },
        (payload) => {
          bumpRealtimeRx('donation_goal_triggers', payload.eventType);
          rtDebug('postgres_changes donation_goal_triggers', { eventType: payload.eventType });
          get().applyDonationGoalTrigger(payload);
        }
      )
      // donation_goal_contributions : sans colonne event_id sur certaines instances → erreur Realtime ;
      // le podium suit les bids (scheduleContributorsRefresh).
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'request_reactions',
          filter: `event_id=eq.${eventId}`,
        },
        ({ eventType }) => {
          bumpRealtimeRx('request_reactions', eventType);
          rtDebug('postgres_changes request_reactions', { eventType });
          get().scheduleGamificationRefresh(eventId);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'event_user_badges',
          filter: `event_id=eq.${eventId}`,
        },
        ({ new: row, eventType }) => {
          bumpRealtimeRx('event_user_badges', eventType);
          rtDebug('postgres_changes event_user_badges', { eventType });
          get().applyGamificationBadgeRow(row);
        }
      )
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        set({ participants: presenceCount(state) });
      })
      .subscribe(async (status, err) => {
        rtDebug('canal: statut', {
          status,
          err: err ? (err.message ?? String(err)) : null,
          eventId,
        });
        if (typeof window !== 'undefined' && isRealtimeDebugEnabled()) {
          window.__autodjRealtimeChannel = { status, eventId, err: err?.message ?? null };
        }
        if (status === 'SUBSCRIBED') {
          // Reconnexion (déjà 'reconnecting' avant ce SUBSCRIBED) : on rattrape tout
          // ce qui a pu être manqué pendant la coupure via un resync complet.
          const wasReconnecting = get().liveStatus === 'reconnecting';
          set({ liveStatus: 'live' });
          await get().syncPresenceDisplay();
          if (wasReconnecting) {
            void get().resyncEventData();
          }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          rtDebug('canal: erreur', { status, err: err?.message ?? String(err ?? '') });
          set({ liveStatus: 'reconnecting' });
        } else if (status === 'CLOSED') {
          set({ liveStatus: 'closed' });
        }
      });

    set({ channel });
  },

  applyRealtime(payload) {
    const { eventType, new: newRow, old: oldRow } = payload;
    rtDebug('apply requests', {
      eventType,
      id: newRow?.id ?? oldRow?.id,
      oldStatus: oldRow?.status,
      newStatus: newRow?.status,
    });
    const current = get().requests;
    const bids = get().eventBids;

    if (eventType === 'INSERT') {
      if (
        newRow &&
        ACTIVE_STATUSES.includes(newRow.status) &&
        !current.some((r) => r.id === newRow.id)
      ) {
        set({
          requests: sortByPriority(normalizeRequests([newRow, ...current], bids)),
        });
        get().ensureBarUsernameLoaded(newRow.bar_user_id);
      }
      // Pas de resync cagnotte ici : elle ne dépend que des bids (couverts par
      // applyDonationBidRealtime). Une suggestion/vote ne bouge pas le pot.
      return;
    }

    if (eventType === 'UPDATE') {
      const becamePlayNext =
        oldRow?.status !== 'play_next' && newRow?.status === 'play_next';
      const tipCentsPreAuth = get()
        .eventBids.filter(
          (b) =>
            b.request_id === newRow.id &&
            (b.payment_status === 'paid' || b.payment_status === 'pending')
        )
        .reduce((s, b) => s + (Number(b.amount) || 0), 0);
      if (becamePlayNext && tipCentsPreAuth > 0) {
        void get().showAcceptedSongPopup(newRow, tipCentsPreAuth);
      }

      const isActive = newRow && ACTIVE_STATUSES.includes(newRow.status);
      let next;
      if (!isActive) {
        next = current.filter((r) => r.id !== newRow.id);
      } else {
        const exists = current.some((r) => r.id === newRow.id);
        next = exists
          ? current.map((r) => (r.id === newRow.id ? { ...r, ...newRow } : r))
          : [newRow, ...current];
      }
      set({ requests: sortByPriority(normalizeRequests(next, bids)) });
      get().ensureBarUsernameLoaded(newRow.bar_user_id);
      if (Number(oldRow?.votes ?? 0) !== Number(newRow?.votes ?? 0)) {
        const evt = get().event;
        if (evt?.id) get().scheduleVotePodiumRefresh(evt.id);
      }
      return;
    }

    if (eventType === 'DELETE' && oldRow) {
      set({ requests: current.filter((r) => r.id !== oldRow.id) });
      const evt = get().event;
      if (evt?.id && oldRow?.bar_user_id) {
        get().scheduleVotePodiumRefresh(evt.id);
      }
    }
  },

  async showAcceptedSongPopup(row, amountCentsOverride) {
    if (!row) return;

    let donorName = '';
    const barUserId = row.bar_user_id ?? null;
    if (barUserId) {
      donorName = get().usernameByBarUserId[barUserId] ?? '';
      if (!donorName) {
        const { data } = await supabase
          .from('bar_users')
          .select('username')
          .eq('id', barUserId)
          .maybeSingle();
        donorName = data?.username ?? '';
        if (donorName) {
          set({
            usernameByBarUserId: {
              ...get().usernameByBarUserId,
              [barUserId]: donorName,
            },
          });
        }
      }
    }
    if (!donorName) donorName = barUserId ? 'Un participant' : 'Un invité';

    const paidFromBids =
      typeof amountCentsOverride === 'number'
        ? amountCentsOverride
        : get()
            .eventBids.filter(
              (b) =>
                b.request_id === row.id &&
                (b.payment_status === 'paid' || b.payment_status === 'pending')
            )
            .reduce((s, b) => s + (Number(b.amount) || 0), 0);

    const popup = {
      id: row.id,
      song: row.custom_title ?? 'Une track',
      artist: row.custom_artist ?? '',
      donor: donorName,
      donorBarUserId: barUserId,
      amountCents: Math.max(0, paidFromBids),
      at: Date.now(),
    };

    set({ acceptedSong: popup });

    // Auto-dismiss après 6 s, sauf si remplacé entretemps par un autre popup.
    setTimeout(() => {
      const current = get().acceptedSong;
      if (current && current.at === popup.at) {
        set({ acceptedSong: null });
      }
    }, 6000);
  },

  dismissAcceptedSong() {
    set({ acceptedSong: null });
  },

  applyDonationBidRealtime(payload) {
    const { eventType, new: newRow, old: oldRow } = payload;
    rtDebug('apply bids', {
      eventType,
      id: newRow?.id ?? oldRow?.id,
      oldPay: oldRow?.payment_status,
      newPay: newRow?.payment_status,
    });
    let bids = [...get().eventBids];

    if (eventType === 'INSERT' && newRow) {
      // Dedup : un même INSERT peut arriver deux fois (double canal transitoire).
      if (!bids.some((b) => b.id === newRow.id)) bids.push(newRow);
    } else if (eventType === 'UPDATE' && newRow) {
      bids = bids.map((b) => (b.id === newRow.id ? newRow : b));
    } else if (eventType === 'DELETE' && oldRow) {
      bids = bids.filter((b) => b.id !== oldRow.id);
    }

    set({
      eventBids: bids,
      requests: sortByPriority(normalizeRequests(get().requests, bids)),
    });

    const wasPaid = oldRow?.payment_status === 'paid';
    const isPaid = newRow?.payment_status === 'paid';
    const amountChanged =
      eventType === 'UPDATE' && (Number(oldRow?.amount) || 0) !== (Number(newRow?.amount) || 0);
    const barUserChanged =
      eventType === 'UPDATE' && oldRow?.bar_user_id !== newRow?.bar_user_id;

    // Tout ce qui peut faire bouger la cagnotte ou le podium :
    // INSERT/DELETE d'un bid paid, bascule paid↔non-paid, ou mutation du montant/du donateur sur un bid paid.
    const affectsTotals =
      (eventType === 'INSERT' && isPaid) ||
      (eventType === 'DELETE' && wasPaid) ||
      (eventType === 'UPDATE' &&
        (wasPaid !== isPaid ||
          (wasPaid && isPaid && (amountChanged || barUserChanged))));

    if (affectsTotals) {
      const evt = get().event;
      if (evt?.id) {
        get().scheduleContributorsRefresh(evt.id);
        get().scheduleDonationGoalResync();
      }
    }

    const becamePaid =
      isPaid &&
      (eventType === 'INSERT' ||
        (eventType === 'UPDATE' && !wasPaid));

    if (becamePaid) {
      get().pushBoostFeedFromBid(newRow);
    }
  },

  applyDonationGoalTrigger(payload) {
    const stepId = payload?.new?.step_id;
    if (!stepId) return;
    const currentGoal = get().donationGoal;
    if (!currentGoal) return;

    const nextSteps = currentGoal.steps.map((step) =>
      step.id === stepId ? { ...step, is_triggered: true } : step
    );

    set((state) => ({
      donationGoal: {
        ...currentGoal,
        steps: nextSteps,
      },
      donationGoalPulse: state.donationGoalPulse + 1,
    }));
  },

  async unsubscribe() {
    const channel = get().channel;
    if (channel) {
      set({ channel: null });
      try {
        await supabase.removeChannel(channel);
      } catch {
        // ignore
      }
    }
  },

  async voteFor(requestId) {
    const { event, votedRequestIds } = get();
    if (!event) return { ok: false, reason: 'no_event' };
    if (votedRequestIds.has(requestId)) return { ok: false, reason: 'already_voted' };

    const clientUid = getClientUid();
    const barUid = get().barUser?.id ?? null;

    // La contrainte unique(request_id, client_uid) couvre le même appareil ; ce
    // pré-check ajoute la dédup cross-appareil par bar_user_id (même compte, 2 tel).
    {
      let dedupQuery = supabase
        .from('votes')
        .select('id', { count: 'exact', head: true })
        .eq('request_id', requestId)
        .limit(1);
      dedupQuery = barUid
        ? dedupQuery.or(`client_uid.eq.${clientUid},bar_user_id.eq.${barUid}`)
        : dedupQuery.eq('client_uid', clientUid);
      const { count, error: dedupError } = await dedupQuery;
      if (!dedupError && (count ?? 0) > 0) {
        const next = new Set(votedRequestIds);
        next.add(requestId);
        set({ votedRequestIds: next });
        return { ok: false, reason: 'already_voted' };
      }
    }

    const nextVoted = new Set(votedRequestIds);
    nextVoted.add(requestId);
    const br = aggregateBidsByRequestId(get().eventBids);
    set({
      votedRequestIds: nextVoted,
      requests: get().requests.map((r) =>
        r.id === requestId
          ? normalizeRequest({ ...r, votes: (r.votes ?? 0) + 1 }, br)
          : r
      ),
    });

    function rollbackOptimistic() {
      const rolled = new Set(get().votedRequestIds);
      rolled.delete(requestId);
      const br2 = aggregateBidsByRequestId(get().eventBids);
      set({
        votedRequestIds: rolled,
        requests: get().requests.map((r) =>
          r.id === requestId
            ? normalizeRequest({ ...r, votes: Math.max(0, (r.votes ?? 1) - 1) }, br2)
            : r
        ),
      });
    }

    const { error: voteError } = await supabase
      .from('votes')
      .insert({
        request_id: requestId,
        event_id: event.id,
        client_uid: clientUid,
        bar_user_id: barUid,
      });

    if (voteError) {
      rollbackOptimistic();
      return {
        ok: false,
        reason: voteError.code === '23505' ? 'already_voted' : 'error',
        error: voteError,
      };
    }

    // Le compteur requests.votes et le priority_score sont maintenus côté DB par
    // le trigger trg_votes_maintain_count : rien à incrémenter depuis le client.
    get().scheduleVotePodiumRefresh(event.id);

    return { ok: true, added: true };
  },

  async removeVote(requestId) {
    const { event, votedRequestIds } = get();
    if (!event) return { ok: false, reason: 'no_event' };
    if (!votedRequestIds.has(requestId)) return { ok: false, reason: 'not_voted' };

    const clientUid = getClientUid();
    const barUid = get().barUser?.id ?? null;

    const nextVoted = new Set(votedRequestIds);
    nextVoted.delete(requestId);
    const br = aggregateBidsByRequestId(get().eventBids);
    set({
      votedRequestIds: nextVoted,
      requests: get().requests.map((r) =>
        r.id === requestId
          ? normalizeRequest({ ...r, votes: Math.max(0, (r.votes ?? 1) - 1) }, br)
          : r
      ),
    });

    function rollbackOptimisticRemove() {
      const rolled = new Set(get().votedRequestIds);
      rolled.add(requestId);
      const br2 = aggregateBidsByRequestId(get().eventBids);
      set({
        votedRequestIds: rolled,
        requests: get().requests.map((r) =>
          r.id === requestId
            ? normalizeRequest({ ...r, votes: (r.votes ?? 0) + 1 }, br2)
            : r
        ),
      });
    }

    const { data, error } = await supabase.rpc('remove_vote', {
      p_event_id: event.id,
      p_request_id: requestId,
      p_client_uid: clientUid,
      p_bar_user_id: barUid ?? null,
    });

    if (error) {
      rollbackOptimisticRemove();
      return { ok: false, reason: 'error', error };
    }

    const removed = Number(data) > 0;
    if (!removed) {
      rollbackOptimisticRemove();
      get().scheduleVotePodiumRefresh(event.id);
      return { ok: false, reason: 'not_found' };
    }

    get().scheduleVotePodiumRefresh(event.id);
    return { ok: true, removed: true };
  },

  async toggleVoteFor(requestId) {
    if (get().votedRequestIds.has(requestId)) {
      return get().removeVote(requestId);
    }
    return get().voteFor(requestId);
  },

  myActiveRequestsCount() {
    const clientUid = getClientUid();
    const barUid = get().barUser?.id ?? null;
    return get().requests.filter(
      (r) => r.client_uid === clientUid || (barUid && r.bar_user_id === barUid)
    ).length;
  },

  async suggestTrack({
    title,
    artist,
    bidAmount = 0,
    deezerTrackId = null,
    deezerPreviewUrl = null,
    deezerCoverUrl = null,
  }) {
    const { event } = get();
    if (!event) return { ok: false, reason: 'no_event' };

    const clientUid = getClientUid();
    const cleanTitle = (title ?? '').trim();
    const cleanArtist = (artist ?? '').trim();
    if (!cleanTitle || !cleanArtist) {
      return { ok: false, reason: 'missing_fields' };
    }

    const amount = Math.max(0, Math.floor(Number(bidAmount) || 0));
    if (amount > 0) {
      return {
        ok: false,
        reason: 'paid_requires_checkout',
        message: 'Les suggestions avec montant doivent passer par le paiement en ligne avant création en base.',
      };
    }

    const normalizedDeezerTrackId =
      deezerTrackId === null || deezerTrackId === undefined
        ? null
        : Number(deezerTrackId);

    const barUid = get().barUser?.id ?? null;

    const { data, error } = await supabase
      .from('requests')
      .insert({
        event_id: event.id,
        custom_title: cleanTitle,
        custom_artist: cleanArtist,
        deezer_track_id:
          Number.isFinite(normalizedDeezerTrackId) && normalizedDeezerTrackId > 0
            ? Math.floor(normalizedDeezerTrackId)
            : null,
        deezer_preview_url: deezerPreviewUrl ?? null,
        deezer_cover_url: deezerCoverUrl ?? null,
        client_uid: clientUid,
        bar_user_id: barUid,
      })
      .select('*')
      .single();

    if (error) {
      return {
        ok: false,
        reason: 'error',
        message: error.message,
        details: error.details ?? error.hint,
        error,
      };
    }

    const bidsList = [...get().eventBids];

    const current = get().requests;
    const nextReqs = current.some((r) => r.id === data.id)
      ? current.map((r) => (r.id === data.id ? { ...r, ...data } : r))
      : [data, ...current];

    set({
      eventBids: bidsList,
      requests: sortByPriority(normalizeRequests(nextReqs, bidsList)),
    });

    markSuggestionSent();
    pushHistoryEntry({
      id: data.id,
      title: cleanTitle,
      artist: cleanArtist,
      bid_amount: amount,
    });

    return { ok: true, request: data };
  },

  async boostTrack(requestId, amountCents) {
    const event = get().event;
    if (!event) return { ok: false, reason: 'no_event' };

    const amt = Math.max(0, Math.floor(Number(amountCents) || 0));
    if (amt < 20) {
      return { ok: false, reason: 'amount_too_low' };
    }

    const snapshot = [...get().requests];
    const idx = snapshot.findIndex((r) => r.id === requestId);
    if (idx < 0) return { ok: false, reason: 'not_found' };

    const req = snapshot[idx];
    if (req.status !== 'pending') {
      return {
        ok: false,
        reason: 'not_boostable',
        message:
          req.status === 'play_next'
            ? 'Cette suggestion est déjà acceptée par le DJ — ajout de montant impossible.'
            : 'Cette suggestion ne peut plus recevoir de montant.',
      };
    }

    const clientUid = getClientUid();
    const barUid = get().barUser?.id ?? null;

    const { data: newBidId, error: rpcError } = await supabase.rpc('apply_music_boost', {
      p_event_id: event.id,
      p_request_id: requestId,
      p_amount_cents: amt,
      p_client_uid: clientUid,
      // Toujours envoyer la clé (null explicite) pour que PostgREST résolve la signature à 5 args.
      p_bar_user_id: barUid ?? null,
    });

    if (rpcError) {
      // eslint-disable-next-line no-console
      console.warn('[boost] apply_music_boost', rpcError);
      return {
        ok: false,
        reason: 'rpc_failed',
        message: rpcError.message,
        error: rpcError,
      };
    }

    let bidIdForCheckout =
      newBidId === null || newBidId === undefined || newBidId === ''
        ? null
        : String(newBidId).trim();
    if (!bidIdForCheckout) {
      const { data: lastBid, error: bidLookupErr } = await supabase
        .from('bids')
        .select('id')
        .eq('request_id', requestId)
        .eq('event_id', event.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!bidLookupErr && lastBid?.id) bidIdForCheckout = lastBid.id;
    }

    const { data: bidsRows, error: bidsErr } = await supabase
      .from('bids')
      .select(BID_SELECT)
      .eq('event_id', event.id);

    if (bidsErr) {
      // eslint-disable-next-line no-console
      console.warn('[boost] reload bids', bidsErr);
    }

    const bidsList = bidsRows ?? get().eventBids;

    set({
      eventBids: bidsList,
      requests: sortByPriority(normalizeRequests(get().requests, bidsList)),
    });

    get().scheduleContributorsRefresh(event.id);
    get().scheduleDonationGoalResync();

    return { ok: true, bidId: bidIdForCheckout };
  },

  reset() {
    // Annule tout loadEvent en vol + purge les timers debounce armés : sinon un
    // timer refire après le reset et repeuple podium/cagnotte d'une autre soirée.
    loadGeneration += 1;
    clearEventDebounceTimers();
    void get().unsubscribe();
    set({
      loading: true,
      error: null,
      event: null,
      establishment: null,
      requests: [],
      votedRequestIds: new Set(),
      liveStatus: 'idle',
      participants: 0,
      donationGoal: null,
      donationGoalPulse: 0,
      eventPotCents: 0,
      eventBids: [],
      liveBoostFeed: [],
      topContributors: [],
      votePodium: [],
      acceptedSong: null,
      usernameByBarUserId: {},
      featuredBadgeByBarUserId: {},
      reactionCountsByRequest: {},
      myReactionKeysByRequest: {},
      eventBadges: [],
      barAuthSignInError: null,
      barAuthProfileError: null,
      barAuthReason: null,
    });
  },
}));
